import type { AchievementLevel, ConceptChipScoredItem } from '@mathflat/domain/Achievement'
import { ACHIEVEMENT_LEVEL_COUNT_DEFAULTS } from '@mathflat/domain/Achievement'
import type { LessonClassChallengeSetting } from '@mathflat/domain/Challenge'
import type { WeakConceptChipGroupTarget } from '@mathflat/domain/Derivation'
import type { AchievementReportCreateBody } from '@mathflat/domain/Report'
import type { AllGradeSubject, Revision, SchoolType, Semester } from '@mathflat/domain/SchoolSystem'
import type { SchoolTestOrigin } from '@mathflat/domain/SchoolTest'
import type { Student as StudentData } from '@mathflat/domain/Student'
import type {
  SupportedWorkbook,
  SupportedWorkbookType,
  WorkbookListItem,
  WorkbookOf,
} from '@mathflat/domain/Workbook'
import { flatMap } from 'lodash-es'
import { minBy } from 'lodash-es'
import { action, makeAutoObservable, makeObservable, observable, runInAction, toJS } from 'mobx'

import type { AchievementReportAiCommentBody } from '@/api/aiApi'
import { TOAST_STR } from '@/constant'
import { EMPTY_NUMBER_ID, MAX_REQUEST_NUMBER } from '@/constant/common'
import { _workbookController } from '@/controller/_workbook.controller'
import {
  achievementAnalysisController,
  type LessonClassChallengeParams,
} from '@/controller/achievementAnalysis.controller'
import { aiController } from '@/controller/ai.controller.ts'
import { smsController } from '@/controller/sms.controller.ts'
import { DTO, SmsPost, type Student, WorkbookColl, Worksheet } from '@/dto'
import type { AchievementConceptChip } from '@/dto/AchievementAnalysis/AchievementConceptChip.dto'
import type {
  AchievementReport,
  AchievementReportColl,
} from '@/dto/AchievementAnalysis/AchievementReport.dto.ts'
import type { GroupAchievementConceptChipDetail } from '@/dto/AchievementAnalysis/GroupAchievementConceptChipDetail.dto'
import { CheckableMap } from '@/models/CheckableMap'
import commonRepo from '@/repository/common.repo'
import { type CoreItem, curriculumCoreService } from '@/service/curriculumCore.service'
import modalService from '@/service/modal.service'
import WebviewService from '@/service/webview.service.ts'
import type { WorksheetSubTypeCode } from '@/types/learningContent'
import appPostMsg from '@/utils/appPostMsg.ts'
import { remoteStorageService } from '@/utils/axios/remoteStorage'
import { urlPreview } from '@/utils/download'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import type { EventPropertiesSchema } from '@/utils/trackings/schema/eventProperties.schema'

import {
  SchoolTestFilterManager,
  type SchoolTestFilterOptionType,
} from './_SchoolTestFilterManager.service'
import { AchievementConceptChipManager } from './AchievementConceptChipManager.service'
import { getCurriculumKeyForSchool } from './getCurriculumKeyForSchool'

type UIFilterT = {
  recommended: boolean
  achievementLevelSet: Set<AchievementLevel>
}

const createDefaultUIFilter = (): UIFilterT => ({
  recommended: false,
  achievementLevelSet: new Set<AchievementLevel>(),
})

export type BaseExternalData = {
  __version: `v${number}`
  __timestamp: number
}

export type AnalysisAchievementExternal =
  | AnalysisAchievementExternalV1
  | AnalysisAchievementExternalV2
  | AnalysisAchievementExternalV3

export type AnalysisAchievementExternalV1 = Record<
  StudentData['id'],
  {
    grade: CoreItem['gradeSubject']
    schoolType: CoreItem['schoolType']
    semester: CoreItem['semester']
  }
>

export type AnalysisAchievementExternalV2 = Record<
  StudentData['id'],
  {
    schoolType: SchoolType
    grade: AllGradeSubject
    semester: Semester
    workbookFilterOptionsByCurriculumKey?: {
      [key: string]: {
        workbooks: SupportedWorkbook[]
      }
    }
    schoolTestFilterOptionsByCurriculumKey?: SchoolTestFilterOptionsByCurriculumKey
  }
>

export type AnalysisAchievementExternalV3 = Record<
  StudentData['id'],
  {
    schoolType: SchoolType
    grade: AllGradeSubject
    semester: Semester
    revision: Revision
    workbookFilterOptionsByCurriculumKey?: WorkbookFilterOptionsByCurriculumKey
    schoolTestFilterOptionsByCurriculumKey?: SchoolTestFilterOptionsByCurriculumKey
    useRecommendedFilter?: boolean
    /** 교재·기출과 달리 학년·학기를 가리지 않는 축이라 커리큘럼 아래가 아니라 여기 둔다 */
    useArithmeticFilter?: boolean
  }
> &
  Record<'__version', `v${number}`>

type WorkbookFilterOptionsByCurriculumKey = {
  [key: string]: {
    workbooks: Pick<SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>[]
  }
}

export type SchoolTestDataForService = Pick<SchoolTestOrigin, 'id' | 'title' | 'school'>

type SchoolTestFilterOptionsByCurriculumKey = {
  [key: string]: {
    schoolTests: SchoolTestDataForService[]
    schoolTestFilterOption: SchoolTestFilterOptionType
  }
}

export class AchievementAnalysisService {
  static REMOTE_STORAGE_VERSION = 3
  achievementConceptChipManager = new AchievementConceptChipManager() // 유형칩 관리 서비스
  schoolSearchFilterManager = new SchoolTestFilterManager() // 학교별 기출필터 서비스
  uiStore = new UIStore()

  selectedStudentId?: string

  selectedChipId = EMPTY_NUMBER_ID // 선택된 유형칩
  selectedConceptChipDetail: GroupAchievementConceptChipDetail | null = null
  studyLogMap = new Map<AchievementConceptChip['conceptChipId'], ConceptChipScoredItem[]>() // 선택된 유형칩의 최근 학습 이력
  checkedConceptChipIdSet = new Set<AchievementConceptChip['id']>() // 체크된 유형칩 id Set

  workbookResponseFilterColls: {
    [key in SupportedWorkbookType]: WorkbookColl<WorkbookListItem<WorkbookOf<key>>> | undefined
  } = {
    CUSTOM_SIGNATURE: undefined,
    ACADEMY_GROUP: undefined,
    PUBLIC: undefined,
    SCHOOL: undefined,
  } // 필터 - 시그니처교재, 학원그룹교재, 시중교재, 교과서(리모트스토리이지에서 가져온 값)

  reportPdfInfo?: { pdf: { minPage: number; maxPage: number } } // 유형분석 보고서 정보
  pdfClient: { connected: boolean } = { connected: false } // pdf 클라이언트 연결 여부
  achievementReportColl: AchievementReportColl | null = null // 유형분석 보고서 목록
  selectedLessonClassChallenge: LessonClassChallengeSetting | null = null

  filterResolved = false // 진입 필터 복원이 끝나 쿼리를 켜도 되는지 여부
  pendingSelectedConceptChipId?: number // sessionStorage 진입 시 칩 주입 후 1회 사전선택할 conceptChipId

  constructor() {
    makeAutoObservable(this, {
      achievementReportColl: observable.ref,
    })
  }

  unselectChipId = () => {
    this.selectedChipId = EMPTY_NUMBER_ID
  }

  setFilterResolved(v: boolean) {
    this.filterResolved = v
  }

  setPendingSelectedConceptChipId(id?: number) {
    this.pendingSelectedConceptChipId = id
  }

  static koBookType = (
    bookType: ConceptChipScoredItem['type'],
    subType: ConceptChipScoredItem['subType'],
  ) => {
    if (bookType === 'WORKBOOK') return '교재'
    if (bookType === 'WORKSHEET') {
      // 학습지 내에 "챌린지" / "자기주도학습"이 포함됨.
      // 학습지 출처의 subType 은 학습지 하위 종류 코드다. 고른 코드가 아니면 fallbackText 가 나온다
      return Worksheet.subTypeKr(subType as WorksheetSubTypeCode, {
        availableSubTypes: [12, 13, 14, 15, 16],
        fallbackText: '학습지',
      })
    }

    return ''
  }

  private achievementFilter(achievementConceptChip: AchievementConceptChip) {
    const achievementLevels = [...this.uiStore.filter.achievementLevelSet.values()]

    if (achievementLevels.length === 0) return true

    return (
      achievementConceptChip.achievementLevel &&
      achievementLevels.includes(achievementConceptChip.achievementLevel)
    )
  }

  private requiredFilter(achievementConceptChip: AchievementConceptChip) {
    if (!this.uiStore.filter.recommended) return true

    return achievementConceptChip.recommended
  }

  get isFunctionDisabled() {
    return {
      checkAchievementChip: !!this.achievementConceptChipManager.filter.requestDate,
    }
  }

  get filteredAchievementConceptChips() {
    return this.achievementConceptChipManager.achievementConceptChips.filter(
      (achievementConceptChip) =>
        this.achievementFilter(achievementConceptChip) &&
        this.requiredFilter(achievementConceptChip),
    )
  }

  get requiredFilteredAchievementConceptChips() {
    return this.achievementConceptChipManager.achievementConceptChips.filter(
      (achievementConceptChip) => this.requiredFilter(achievementConceptChip),
    )
  }

  get checkedFilteredAchievementConceptChips() {
    return this.filteredAchievementConceptChips.filter((chip) =>
      this.checkedConceptChipIdSet.has(chip.id),
    )
  }

  get checkedFilteredCountByAchievementLevel() {
    const result = this.checkedFilteredAchievementConceptChips.reduce(
      (prev, { achievementLevel }) => {
        if (!achievementLevel) return prev

        return {
          ...prev,
          [achievementLevel]: prev[achievementLevel] + 1,
        }
      },
      ACHIEVEMENT_LEVEL_COUNT_DEFAULTS,
    )

    // convert for data tracking
    const data = {} as Record<Lowercase<AchievementLevel>, number>
    Object.entries(result).forEach(([k, v]) => {
      data[k.toLowerCase()] = v
    })

    return data
  }

  get countByAchievementLevel(): Record<AchievementLevel, number> {
    return this.achievementConceptChipManager.achievementConceptChips
      .filter((v) => this.requiredFilter(toJS(v)))
      .reduce((prev, { achievementLevel }) => {
        if (!achievementLevel) return prev

        return {
          ...prev,
          [achievementLevel]: prev[achievementLevel] + 1,
        }
      }, ACHIEVEMENT_LEVEL_COUNT_DEFAULTS)
  }

  get prevCurNextDetailInfo() {
    const targetIndex = this.checkedFilteredAchievementConceptChips.findIndex(
      ({ conceptChipId }) => conceptChipId === this.selectedChipId,
    )

    const getDetailInfo = (index) => {
      const achievementConceptChip = this.checkedFilteredAchievementConceptChips[index]

      if (!achievementConceptChip) {
        return null
      }

      const studyLogs = this.studyLogMap.get(achievementConceptChip.conceptChipId)

      return { achievementConceptChip, studyLogs }
    }

    const prev = getDetailInfo(targetIndex - 1)
    const current = getDetailInfo(targetIndex)
    const next = getDetailInfo(targetIndex + 1)

    return {
      prev,
      current,
      next,
    }
  }

  /** 연산 문항은 22개정만 있어서 15개정 과목에 연산만 보기를 켜면 항상 빈 결과가 온다 */
  get isArithmeticUnsupportedCurriculum() {
    return (
      curriculumCoreService.getItemByTrieKey(
        this.achievementConceptChipManager.filter.curriculumKey,
      )?.revision === 'CURRICULUM_15'
    )
  }

  get gradeSemester() {
    return (
      curriculumCoreService.getItemByTrieKey(
        this.achievementConceptChipManager.filter.curriculumKey,
      )?.labelShort ?? '중1-1'
    )
  }

  get pdfRenderData() {
    return {
      ...this.reportPdfInfo,
    }
  }

  nextConceptChip() {
    if (this.prevCurNextDetailInfo.next) {
      this.selectedChipId = this.prevCurNextDetailInfo.next.achievementConceptChip.conceptChipId
    }
  }

  prevConceptChip() {
    if (this.prevCurNextDetailInfo.prev) {
      this.selectedChipId = this.prevCurNextDetailInfo.prev.achievementConceptChip.conceptChipId
    }
  }

  updateSelectedChipId() {
    this.selectedChipId =
      this.checkedFilteredAchievementConceptChips.sort(
        (x, y) => x.orderingNumber - y.orderingNumber,
      )[0]?.id ?? EMPTY_NUMBER_ID
  }

  async fetchAchievementStudyLogs(studentId: StudentData['id']) {
    if (
      !this.prevCurNextDetailInfo.current ||
      this.studyLogMap.get(this.prevCurNextDetailInfo.current.achievementConceptChip.conceptChipId)
    )
      return

    const conceptChipId = this.prevCurNextDetailInfo.current.achievementConceptChip.conceptChipId
    const { arithmeticOnly } = this.achievementConceptChipManager.filter

    const data = await achievementAnalysisController.getAchievementStudyLogs(
      studentId,
      conceptChipId,
      { arithmeticOnly },
    )

    // 대기 중 축이 바뀌면 studyLogMap.clear() 로 비운 자리에 이전 축 응답이 다시 들어가고,
    // 위 진입 가드가 그걸 캐시로 인정해 재요청까지 막는다
    if (this.achievementConceptChipManager.filter.arithmeticOnly !== arithmeticOnly) return

    this.studyLogMap.set(conceptChipId, data)

    return data
  }

  isChecked(conceptChipId: AchievementConceptChip['id']) {
    return this.checkedConceptChipIdSet.has(conceptChipId)
  }

  toggleCheck(ids: AchievementConceptChip['id'][]) {
    const status = this.getCheckedStatus(ids)
    let targetId: undefined | number

    if (status === 'EVERY') {
      ids.forEach((id) => this.checkedConceptChipIdSet.delete(id))

      // 선택한 그룹이 모두 취소되므로 전체 칩 중에서 새로 선택 id를 뽑음
      targetId = minBy(
        this.filteredAchievementConceptChips.filter(({ id }) =>
          [...this.checkedConceptChipIdSet.values()].includes(id),
        ),
        'orderingNumber',
      )?.id
    } else {
      ids.forEach((id) => this.checkedConceptChipIdSet.add(id))

      // 선택한 그룹 내에서 선택 id를 뽑음
      targetId = minBy(
        this.filteredAchievementConceptChips.filter(({ id }) => ids.includes(id)),
        'orderingNumber',
      )?.id

      _mixpanelUtils.uiTrack('유형분석_상세정보_API', {
        ...this.checkedFilteredCountByAchievementLevel,
        '선택한 유형칩 수': this.checkedFilteredAchievementConceptChips.length,
      })
    }

    this.selectedChipId = targetId ?? EMPTY_NUMBER_ID
  }

  getCheckedStatus(ids: AchievementConceptChip['id'][]) {
    const checkedTargetIds = [...this.checkedConceptChipIdSet.values()].filter((id) =>
      ids.includes(id),
    )

    if (ids.length === checkedTargetIds.length) {
      return 'EVERY'
    }
    if (checkedTargetIds.length === 0) {
      return 'EMPTY'
    }
    return 'SOME'
  }

  getWrongProblemCountOfCheckedFilteredConceptChips() {
    const checkedFilteredIds = this.checkedFilteredAchievementConceptChips.map(({ id }) => id)
    const studyLogsOfCheckedConceptChip = checkedFilteredIds.map((id) => this.studyLogMap.get(id))

    return studyLogsOfCheckedConceptChip.reduce(
      (prev, cur) => prev + (cur?.filter(({ result }) => result !== 'CORRECT').length ?? 0),
      0,
    )
  }

  /* 기본 필터 관련 로직 */
  loadStoredFilter(storageKey: string) {
    const storedInfo = remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]

    const recommended = storedInfo?.useRecommendedFilter ?? false
    this.uiStore.filter.recommended = recommended

    // 축에 매인 것(학습내역·상세·선택·보고서 드로어)까지 함께 끊어야 한다. 호출부마다 비우는
    // 범위가 달라 그룹 전환에서는 이전 축의 상세가 남았다
    const arithmeticOnly = storedInfo?.useArithmeticFilter ?? false
    this.updateFilterArithmeticOnly(arithmeticOnly)

    // 축이 켜져 있으면 교재·기출은 잠겨 화면에 안 보인다. 되살리면 보이지 않는 값이
    // 그대로 서버로 나간다 (비우는 것은 위에서 이미 했다)
    if (arithmeticOnly) return

    const workbooks =
      storedInfo?.workbookFilterOptionsByCurriculumKey?.[
        this.achievementConceptChipManager.filter.curriculumKey
      ]?.workbooks ?? []

    this.updateFilterWorkbook(
      'CUSTOM_SIGNATURE',
      workbooks.filter((wb) => wb.type === 'CUSTOM_SIGNATURE'),
    )
    this.updateFilterWorkbook(
      'ACADEMY_GROUP',
      workbooks.filter((wb) => wb.type === 'ACADEMY_GROUP'),
    )
    this.updateFilterWorkbook(
      'PUBLIC',
      workbooks.filter((wb) => wb.type === 'PUBLIC'),
    )
    this.updateFilterWorkbook(
      'SCHOOL',
      workbooks.filter((wb) => wb.type === 'SCHOOL'),
    )

    if (!commonRepo.isSchoolflat) {
      const schoolTests =
        storedInfo?.schoolTestFilterOptionsByCurriculumKey?.[
          this.achievementConceptChipManager.filter.curriculumKey
        ]?.schoolTests ?? []
      const schoolTestFilterOption =
        storedInfo?.schoolTestFilterOptionsByCurriculumKey?.[
          this.achievementConceptChipManager.filter.curriculumKey
        ]?.schoolTestFilterOption ?? undefined

      this.updateFilterSchoolTest(schoolTests, schoolTestFilterOption)
    }
  }

  // 교재 필터 데이터를 반환하는 helper 함수
  private getWorkbooksFilterData(storageKey: string) {
    if (!storageKey) return {}

    const newWorkbooks = flatMap(this.achievementConceptChipManager.filter.workbookList)
    const newSet = new Set<Pick<SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>>(
      newWorkbooks,
    )

    return {
      workbookFilterOptionsByCurriculumKey: {
        ...remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
          ?.workbookFilterOptionsByCurriculumKey,
        [this.achievementConceptChipManager.filter.curriculumKey]: {
          workbooks: [...newSet.values()].map((workbook) => ({
            id: workbook.id,
            type: workbook.type,
            fulltitle: workbook.fulltitle,
            publisher: workbook.publisher,
          })),
        },
      },
    }
  }

  // 학교 시험 필터 데이터를 반환하는 helper 함수
  private getSchoolTestFilterData(storageKey: string) {
    if (commonRepo.isSchoolflat) {
      return {}
    }

    if (!storageKey) return {}

    return {
      schoolTestFilterOptionsByCurriculumKey: {
        ...remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
          ?.schoolTestFilterOptionsByCurriculumKey,
        [this.achievementConceptChipManager.filter.curriculumKey]: {
          schoolTests: this.achievementConceptChipManager.filter.schoolTests,
          schoolTestFilterOption: this.schoolSearchFilterManager.schoolTestFilterOption,
        },
      },
    }
  }

  // 기본 커리큘럼 정보를 반환하는 helper 함수
  private getBasicCurriculumData() {
    const curriculumInfo = curriculumCoreService.getItemByTrieKey(
      this.achievementConceptChipManager.filter.curriculumKey,
    )

    return curriculumInfo
      ? {
          schoolType: curriculumInfo.schoolType,
          grade: curriculumInfo.gradeSubject,
          semester: curriculumInfo.semester,
          useRecommendedFilter: this.uiStore.filter.recommended,
          useArithmeticFilter: this.achievementConceptChipManager.filter.arithmeticOnly,
        }
      : {
          useRecommendedFilter: this.uiStore.filter.recommended,
          useArithmeticFilter: this.achievementConceptChipManager.filter.arithmeticOnly,
        }
  }

  // 통합 필터 저장 함수
  async saveFilterToRemoteStorage(storageKey: string) {
    if (!storageKey) {
      return
    }

    const prevData = remoteStorageService.PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석?.[storageKey]
    const basicData = this.getBasicCurriculumData()
    const workbooksData = this.getWorkbooksFilterData(storageKey)
    const schoolTestData = this.getSchoolTestFilterData(storageKey)

    const mergedData: AnalysisAchievementExternalV3 = {
      [storageKey]: {
        ...prevData,
        ...basicData,
        ...workbooksData,
        ...schoolTestData,
      },
      __version: `v${AchievementAnalysisService.REMOTE_STORAGE_VERSION}`,
    } as AnalysisAchievementExternalV3

    await remoteStorageService.patch('PREVIOUS_SELECTED_CURRICULUM_INFO_유형분석', mergedData)
  }

  updateFilterWorkbook(
    type: keyof AchievementConceptChipManager['filter']['workbookList'],
    workbooks: Pick<SupportedWorkbook, 'id' | 'type' | 'fulltitle' | 'publisher'>[],
  ) {
    this.achievementConceptChipManager.updateFilter({
      workbookList: {
        ...this.achievementConceptChipManager.filter.workbookList,
        [type]: workbooks,
      },
    })
    this.updateSelectedChipId()
  }

  clearContentFilters() {
    this.achievementConceptChipManager.updateFilter({
      workbookList: { CUSTOM_SIGNATURE: [], ACADEMY_GROUP: [], PUBLIC: [], SCHOOL: [] },
      schoolTests: [],
    })
    this.schoolSearchFilterManager.resetSchoolTestFilterOption()
  }

  get hasContentFilter() {
    return (
      this.achievementConceptChipManager.workbookIds.length > 0 ||
      this.achievementConceptChipManager.schoolTestIds.length > 0
    )
  }

  updateFilterArithmeticOnly(arithmeticOnly: boolean) {
    if (arithmeticOnly) this.clearContentFilters()
    // 연산 축은 과거 학습 이력 시작일이 달라 이미 걸린 날짜가 선택 불가 범위일 수 있어 해제한다
    this.achievementConceptChipManager.updateFilter(
      arithmeticOnly ? { arithmeticOnly, requestDate: null } : { arithmeticOnly },
    )
    // studyLogMap 은 conceptChipId 로만 캐시돼 축 전환으로는 무효화되지 않는다
    this.studyLogMap.clear()
    this.selectedConceptChipDetail = null
    // 체크·선택을 남기면 새 축에 없는 conceptChipId 로 상세와 학습내역이 계속 발화한다.
    // 칩 목록 자체는 쿼리 쪽에서 응답과 같은 시점에 갈아끼운다
    this.resetCheckedConceptChipIdSet()
    this.unselectChipId()
    // 보고서 내역 드로어 안에도 보고서 생성 진입점이 있다. 열린 채로 두면 연산 축에서 차단이 우회된다
    this.uiStore.update({ reportOpen: false })
  }

  updateFilterSchoolTest(
    schoolTests: SchoolTestDataForService[],
    schoolTestFilterOption?: Partial<SchoolTestFilterOptionType>,
  ) {
    if (commonRepo.isSchoolflat) return
    // 유형분석 조회 필터 업데이트
    this.achievementConceptChipManager.updateFilter({
      schoolTests: schoolTests,
    })
    // 기출문제조회 조건 필터 업데이트(유형분석조회에 사용하는 교재id가 아니라 교재id를 받아오기 위한 필터로 로컬이나 리모트스토리지에서 관리)
    this.schoolSearchFilterManager.updateInitialSchoolTestFilter(schoolTestFilterOption)
    this.updateSelectedChipId()
  }

  initSchoolTestFilterOptions() {
    if (commonRepo.isSchoolflat) return
    try {
      const options = this.schoolSearchFilterManager.schoolTestFilterOption
      this.schoolSearchFilterManager.setYearOptions(options.year)
      this.schoolSearchFilterManager.setTestTypeOptions(options.schoolTestType)
      this.schoolSearchFilterManager.setSchoolCodes([...options.schoolChecked.values()])
    } catch (err) {
      commonErrorMessage(err)
      throw err
    }
  }

  resetAll() {
    this.studyLogMap.clear()
    this.selectedConceptChipDetail = null
    this.uiStore.resetFilter()
    this.unselectChipId()
    this.resetCheckedConceptChipIdSet()
    this.schoolSearchFilterManager.resetAllFilter()
    this.schoolSearchFilterManager.resetSchoolTestFilterOption()
    this.achievementConceptChipManager.resetFilter()
  }

  resetCheckedConceptChipIdSet() {
    this.checkedConceptChipIdSet.clear()
  }

  pruneCheckedConceptChipIdSet(existingIds: AchievementConceptChip['id'][]) {
    const allow = new Set(existingIds)
    ;[...this.checkedConceptChipIdSet].forEach((id) => {
      if (!allow.has(id)) this.checkedConceptChipIdSet.delete(id)
    })
  }

  setReportColl(coll: AchievementReportColl | null) {
    this.achievementReportColl = coll
  }

  setWorkbookColls(colls: AchievementAnalysisService['workbookResponseFilterColls']) {
    this.workbookResponseFilterColls = colls
  }

  setLessonClassChallenge(challenge: LessonClassChallengeSetting | null) {
    this.selectedLessonClassChallenge = challenge
  }

  resetFilterExceptCurriculumKey() {
    // 축이 꺼지는 경로가 셋이다 — 토글·초기화·학년 변경. 어느 경로로 꺼지든 축에 매인 캐시는
    // 스스로 무효화되지 않는다
    this.studyLogMap.clear()
    this.selectedConceptChipDetail = null
    this.achievementConceptChipManager.updateFilter({
      requestDate: null,
      workbookList: { CUSTOM_SIGNATURE: [], ACADEMY_GROUP: [], PUBLIC: [], SCHOOL: [] },
      schoolTests: [],
      arithmeticOnly: false,
      curriculumKey: this.achievementConceptChipManager.filter.curriculumKey,
    })
    this.schoolSearchFilterManager.resetSchoolTestFilterOption()
  }

  /* --- 교재 팝오버 관련 로직 --- */
  async fetchWorkbookFilterColls() {
    try {
      this.uiStore.loadingWorkbookPopover = true
      const curriculumKey = this.achievementConceptChipManager.filter.curriculumKey
      const curriculumKeyForSchool = getCurriculumKeyForSchool(curriculumKey)
      const [signatureRes, academyGroupRes, publicRes, schoolRes] = await Promise.all([
        _workbookController.fetchWorkbooks<'CUSTOM_SIGNATURE'>({
          type: 'CUSTOM_SIGNATURE',
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
          featureTypes: commonRepo.permission.bySubscription.workbook.signature.featureTypesParams,
          signatureTypes:
            commonRepo.permission.bySubscription.workbook.signature
              .enableAssignSignatureTypesParams,
        }),
        _workbookController.fetchWorkbooks<'ACADEMY_GROUP'>({
          type: 'ACADEMY_GROUP',
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
        }),
        _workbookController.fetchWorkbooks<'PUBLIC'>({
          type: 'PUBLIC',
          curriculumKey,
          size: MAX_REQUEST_NUMBER,
        }),

        _workbookController.fetchWorkbooks<'SCHOOL'>({
          type: 'SCHOOL',
          curriculumKey: curriculumKeyForSchool,
          size: MAX_REQUEST_NUMBER,
        }),
      ])

      this.workbookResponseFilterColls = {
        CUSTOM_SIGNATURE: new WorkbookColl(
          CheckableMap.fromArr(signatureRes.workbookReponses, 'id'),
        ),
        ACADEMY_GROUP: new WorkbookColl(
          CheckableMap.fromArr(academyGroupRes.workbookReponses, 'id'),
        ),
        PUBLIC: new WorkbookColl(CheckableMap.fromArr(publicRes.workbookReponses, 'id')),
        SCHOOL: new WorkbookColl(CheckableMap.fromArr(schoolRes.workbookReponses, 'id')),
      }

      this.uiStore.loadingWorkbookPopover = false
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  updateCurriculumKeyByStudentGrade(
    ...args: Parameters<typeof curriculumCoreService.getDefaultTrieKeyBySchoolGrade>
  ) {
    const targetGradeSemesterCurriculumKey = curriculumCoreService.getDefaultTrieKeyBySchoolGrade(
      ...args,
    )

    if (targetGradeSemesterCurriculumKey) {
      this.achievementConceptChipManager.updateFilter({
        curriculumKey: targetGradeSemesterCurriculumKey,
      })
    }
  }

  updateCurriculumKeyByGradeSemester(
    ...args: Parameters<typeof curriculumCoreService.getItemByGradeSemesterInfo>
  ) {
    const targetGradeSemesterCurriculumKey = curriculumCoreService.getItemByGradeSemesterInfo(
      ...args,
    )?.trieKey

    if (targetGradeSemesterCurriculumKey) {
      this.achievementConceptChipManager.updateFilter({
        curriculumKey: targetGradeSemesterCurriculumKey,
      })
    }
  }

  async fetchGroupConceptChipDetail(
    params: { conceptChipId: number } & WeakConceptChipGroupTarget,
  ) {
    const { arithmeticOnly } = this.achievementConceptChipManager.filter
    const chipDetail = await achievementAnalysisController.getGroupConceptChipDetail({
      ...params,
      arithmeticOnly,
    })
    runInAction(() => {
      // 대기 중 축이 바뀌면 이전 축 상세가 새 축 화면을 덮어쓴다
      if (this.achievementConceptChipManager.filter.arithmeticOnly !== arithmeticOnly) return

      this.selectedConceptChipDetail = chipDetail
    })
  }

  /**
   * 유형칩, 성취도 fetch 후 유형칩 만들기 (학년,학기 드롭다운 변경시)
   */
  async fetchAllAndMakeChips(studentId: Student['id']) {
    this.resetCheckedConceptChipIdSet()
    this.uiStore.makeAchievementChipCleanup(() =>
      this.achievementConceptChipManager.fetchAllAndMakeChips(studentId),
    )
  }

  async createUpdateReport(studentId: Student['id'], payload: AchievementReportCreateBody) {
    return await achievementAnalysisController.createReport(studentId, payload)
  }

  sendSms = async (htmlUrl: string) => {
    const content = `[${commonRepo.academy?.name}]\n{학생 이름} 학생의 유형별 성취도 리포트를 보내드립니다.\n아래 URL을 눌러 바로 확인할 수 있습니다.\n감사합니다.\n보고서 확인 : ${htmlUrl}`

    const params = new SmsPost({
      sendingTarget: 'PARENTS',
      studentIdList: this.selectedStudentId ? [this.selectedStudentId] : [],
      content,
    })
    return smsController.sendSms(params)
  }

  deleteReports = async (reportIds: AchievementReport['id'][]) => {
    return await achievementAnalysisController.deleteReports(reportIds)
  }

  setCheckAll() {
    if (!this.achievementReportColl) return
    this.achievementReportColl.allCheck()
  }

  setCheckAllFailedReports() {
    if (!this.achievementReportColl) return
    this.achievementReportColl.toArr.forEach((report) => {
      if (report.status === 'FAIL') {
        this.achievementReportColl!.setCheck(report.id)
      }
    })
  }

  preview = async (reportId: AchievementReport['id']) => {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewAchievementReport(reportId)
        return
      }

      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await achievementAnalysisController.preview({ reportId })
        return { url: presignedUrl, fullFileName: fileName }
      }

      urlPreview(getPreviewUrl)
    } catch (e) {
      Number(e.status) === 400
        ? toastError(TOAST_STR.reportNotFoundForPreview)
        : commonErrorMessage(e)
    }
  }

  isDuplicateAchievementReport(title: string) {
    const duplicationAchievementReport = this.achievementReportColl?.toArr.find(
      (report) => report.title === title,
    )

    return !!duplicationAchievementReport
  }

  getSessionId = async () => {
    return await aiController.getSessionId()
  }

  createAiComment = async (
    sessionId: string,
    studentId: Student['id'],
    payload: AchievementReportAiCommentBody,
  ) => {
    return await aiController.createAiComment(sessionId, studentId, payload)
  }

  /**
   * 반 학생들 소단원 챌린지 설정, 조회, 해제
   *  */
  postLessonClassChallenges = async (params: LessonClassChallengeParams) => {
    try {
      const challengeSetting = await achievementAnalysisController.postLessonClassChallenges(params)
      runInAction(() => {
        this.selectedLessonClassChallenge = {
          id: challengeSetting.id,
          relationId: challengeSetting.curriculumId,
          relationType: 'LITTLE_CHAPTER',
        }
      })
      toastSuccess('해당 소단원이 전체 학생의 수업으로\n설정되었습니다.')
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  fetchLessonClassChallenges = async (
    lessonClassId: LessonClassChallengeParams['lessonClassId'],
  ) => {
    try {
      const settings = await achievementAnalysisController.getLessonClassChallenges(lessonClassId)

      // 서버에서 확장 가능성을 위해 리스트 형태로 내려주고 있음. 현재는 하나의 값만 사용하기 때문에 0번째 인덱스 값을 사용
      const selectedLessonClassChallenge = settings[0] ?? null
      runInAction(() => {
        this.selectedLessonClassChallenge = selectedLessonClassChallenge
      })
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  updateStudentChallengeStatus = async (challengeId: LessonClassChallengeSetting['id']) => {
    try {
      await achievementAnalysisController.patchLessonClassChallengeStatus({
        id: challengeId,
        status: 'INACTIVE',
      })
      if (this.selectedLessonClassChallenge?.id === challengeId) {
        runInAction(() => {
          this.selectedLessonClassChallenge = null
        })
      }
      toastSuccess('해당 소단원이 전체 학생에게\n초기화 되었습니다.')
      modalService.closeModal()
    } catch (e) {
      commonErrorMessage(e)
    }
  }

  ////// 이벤트 트래킹
  getAchievementAnalysisDataTrackingInfo():
    | Pick<
        EventPropertiesSchema.유형분석조회,
        | '학년학기'
        | '시그니처교재'
        | '학원그룹교재'
        | '시중교재'
        | '교과서'
        | '기출연도'
        | '시험범위'
        | '추천유형만보기'
        | '연산만보기'
      >
    | undefined {
    try {
      const curriculumInfo = curriculumCoreService.getItemByTrieKey(
        this.achievementConceptChipManager.filter.curriculumKey,
      )

      if (!curriculumInfo) {
        return
      }

      return {
        학년학기: curriculumInfo?.labelShort ?? '',
        시그니처교재: this.achievementConceptChipManager.filter.workbookList.CUSTOM_SIGNATURE.map(
          (wb) => wb.fulltitle,
        ),
        학원그룹교재: this.achievementConceptChipManager.filter.workbookList.ACADEMY_GROUP.map(
          (wb) => wb.fulltitle,
        ),
        시중교재: this.achievementConceptChipManager.filter.workbookList.PUBLIC.map(
          (wb) => wb.fulltitle,
        ),
        교과서: this.achievementConceptChipManager.filter.workbookList.SCHOOL.map(
          (wb) => wb.fulltitle,
        ),
        기출연도: this.schoolSearchFilterManager.selectedYearId,
        시험범위:
          this.schoolSearchFilterManager.selectedTestTypeId === 'ALL'
            ? '중간기말 전체'
            : this.schoolSearchFilterManager.selectedTestTypeId === 'MIDTERM'
              ? '중간고사'
              : '기말고사',
        추천유형만보기: this.uiStore.filter.recommended,
        연산만보기: this.achievementConceptChipManager.filter.arithmeticOnly,
      }
    } catch (e) {
      commonErrorMessage(e)
    }
  }
}

class UIStore extends DTO {
  openedModalId = EMPTY_NUMBER_ID
  loadingMakeAchievementChip = true
  detailOpen = false
  loadingWorkbookPopover = true
  filter: UIFilterT = createDefaultUIFilter()
  reportOpen = false

  constructor() {
    super()

    makeObservable(this, {
      openedModalId: observable,
      loadingMakeAchievementChip: observable,
      detailOpen: observable,
      filter: observable,
      resetFilter: action,
      makeAchievementChipCleanup: action,
      loadingWorkbookPopover: observable,
      reportOpen: observable,
    })
  }

  resetFilter() {
    this.filter = createDefaultUIFilter()
  }

  makeAchievementChipCleanup = async (fn: () => void) => {
    this.loadingMakeAchievementChip = true
    await fn()
    this.loadingMakeAchievementChip = false
  }
}
